import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, Webhook, Plus, X, Play, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DEFAULT_HEADERS = [{ key: 'Authorization', value: '' }];

const ManageHooksPage = () => {
  const [hooks, setHooks] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', target_url: '', http_method: 'POST', is_active: true, description: '', event_subscriptions: [], headers_list: DEFAULT_HEADERS });
  const [testing, setTesting] = useState(null);
  const [deliveries, setDeliveries] = useState(null); // {hook, items}
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { load(); loadCatalog(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/hooks'); setHooks(res.data); } catch { setHooks([]); }
    setLoading(false);
  };
  const loadCatalog = async () => {
    try { const res = await api.get('/hooks/event-catalog'); setCatalog(res.data); } catch { setCatalog([]); }
  };

  const toggleSub = (eventName) => {
    const subs = new Set(form.event_subscriptions);
    subs.has(eventName) ? subs.delete(eventName) : subs.add(eventName);
    setForm({ ...form, event_subscriptions: Array.from(subs) });
  };

  const addHeader = () => setForm({ ...form, headers_list: [...form.headers_list, { key: '', value: '' }] });
  const removeHeader = (idx) => setForm({ ...form, headers_list: form.headers_list.filter((_, i) => i !== idx) });
  const updateHeader = (idx, patch) => setForm({ ...form, headers_list: form.headers_list.map((h, i) => (i === idx ? { ...h, ...patch } : h)) });

  const submit = async () => {
    if (!form.name || !form.target_url) { toast.error('Name and target URL required'); return; }
    if (form.event_subscriptions.length === 0) { toast.error('Subscribe to at least one event'); return; }
    const headers = {};
    form.headers_list.forEach(h => { if (h.key) headers[h.key] = h.value; });
    try {
      await api.post('/hooks', { ...form, headers, headers_list: undefined });
      toast.success('Hook created');
      setShowModal(false);
      setForm({ name: '', target_url: '', http_method: 'POST', is_active: true, description: '', event_subscriptions: [], headers_list: DEFAULT_HEADERS });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (h) => {
    try { await api.put(`/hooks/${h.id}`, { is_active: !h.is_active }); load(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    const id = toDelete; setToDelete(null);
    try { await api.delete(`/hooks/${id}`); load(); }
    catch { toast.error('Failed'); }
  };

  const testHook = async (h) => {
    setTesting(h.id);
    try {
      const res = await api.post(`/hooks/${h.id}/test`);
      if (res.data.status === 'success') toast.success(`Hook OK (HTTP ${res.data.http_status || 200})`);
      else toast.error(`Hook test failed: ${res.data.error || 'unknown'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTesting(null);
  };

  const openDeliveries = async (h) => {
    try {
      const res = await api.get(`/hooks/deliveries?hook_id=${h.id}&limit=20`);
      setDeliveries({ hook: h, items: res.data });
    } catch { toast.error('Failed to load history'); }
  };

  return (
    <AdminSubPage title="Manage Hooks" breadcrumb={['Admin', 'System', 'Hooks']} backPath="/dashboard/admin/system" actionLabel="+ Create Hook" onAction={() => setShowModal(true)}>
      <div className="bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-xs text-blue-800">
        <strong>What is this?</strong> Hooks are HTTP subscribers — when an event fires (e.g. <code>loan.disbursed</code>), each active hook receives a POST with the event envelope. Use this to integrate with external systems (CRM, BI, SMS gateway, etc.). For in-app notifications & email/SMS, use <em>Manage External Events</em> instead.
      </div>

      <div className="bg-white border border-gray-200" data-testid="hooks-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Hook</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Target URL</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Events</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            hooks.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400"><Webhook className="w-8 h-8 mx-auto mb-2 text-gray-300" />No hooks configured. Create one to start subscribing to events.</td></tr> :
            hooks.map(h => (
              <tr key={h.id} className="border-b hover:bg-gray-50" data-testid={`hook-row-${h.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-sm">{h.name}</div>
                  <div className="text-[10px] uppercase text-gray-400">{h.http_method}</div>
                </td>
                <td className="px-4 py-3 text-xs font-mono max-w-xs truncate" title={h.target_url}>{h.target_url}</td>
                <td className="px-4 py-3 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(h.event_subscriptions || []).slice(0, 3).map(e => <span key={e} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px]">{e}</span>)}
                    {(h.event_subscriptions || []).length > 3 && <span className="text-gray-400 text-[10px]">+{h.event_subscriptions.length - 3} more</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-center"><Switch checked={!!h.is_active} onCheckedChange={() => toggleActive(h)} data-testid={`hook-toggle-${h.id}`} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => testHook(h)} disabled={testing === h.id} title="Test connectivity" className="text-blue-600" data-testid={`hook-test-${h.id}`}><Play className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openDeliveries(h)} title="Delivery history" data-testid={`hook-history-${h.id}`}><History className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setToDelete(h.id)} title="Delete" className="text-red-500" data-testid={`hook-delete-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="hook-desc">
          <DialogHeader><DialogTitle>Create Hook</DialogTitle></DialogHeader>
          <p id="hook-desc" className="sr-only">Create outbound webhook subscriber</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Slack notifier" data-testid="hook-name" /></div>
              <div><Label>HTTP Method</Label>
                <Select value={form.http_method} onValueChange={v => setForm({...form, http_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="POST">POST</SelectItem><SelectItem value="PUT">PUT</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Target URL *</Label><Input value={form.target_url} onChange={e => setForm({...form, target_url: e.target.value})} placeholder="https://hooks.slack.com/services/..." data-testid="hook-url" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>

            <div>
              <Label className="mb-2 block">Event subscriptions *</Label>
              <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-gray-200 p-2" data-testid="hook-subs">
                {catalog.map(ev => (
                  <label key={ev} className="flex items-center gap-2 text-xs p-1 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={form.event_subscriptions.includes(ev)} onChange={() => toggleSub(ev)} data-testid={`hook-sub-${ev}`} />
                    <code className="text-[11px]">{ev}</code>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">HTTP Headers</Label>
              {form.headers_list.map((h, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Header name" value={h.key} onChange={e => updateHeader(i, { key: e.target.value })} className="flex-1 text-xs" />
                  <Input placeholder="value" value={h.value} onChange={e => updateHeader(i, { value: e.target.value })} className="flex-1 text-xs" />
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeHeader(i)}><X className="w-3 h-3" /></Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={addHeader}><Plus className="w-3 h-3 mr-1" />Add header</Button>
              <p className="text-[10px] text-gray-400 mt-1">Common: <code>Authorization: Bearer &lt;token&gt;</code>, <code>X-API-Key: ...</code></p>
            </div>

            <div className="flex items-center gap-2"><Label>Active</Label><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={submit} className="bg-[#1f1f1f] text-white" data-testid="hook-submit">Create Hook</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deliveries modal */}
      <Dialog open={!!deliveries} onOpenChange={(o) => !o && setDeliveries(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="hd-desc">
          <DialogHeader><DialogTitle>Delivery history — {deliveries?.hook?.name}</DialogTitle></DialogHeader>
          <p id="hd-desc" className="sr-only">Last 20 deliveries</p>
          {deliveries && (
            <table className="w-full text-xs" data-testid="hook-deliveries-table">
              <thead className="bg-gray-50"><tr>
                <th className="px-2 py-1.5 text-left">When</th><th className="px-2 py-1.5 text-left">Event</th>
                <th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-left">HTTP</th>
                <th className="px-2 py-1.5 text-left">Error</th>
              </tr></thead>
              <tbody className="divide-y">
                {deliveries.items.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-gray-400">No deliveries yet.</td></tr>}
                {deliveries.items.map(d => (
                  <tr key={d.id}>
                    <td className="px-2 py-1 font-mono">{d.attempted_at?.slice(0,19).replace('T',' ')}</td>
                    <td className="px-2 py-1"><code>{d.event_name}</code></td>
                    <td className={`px-2 py-1 uppercase ${d.status === 'success' ? 'text-emerald-600' : d.status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>{d.status}</td>
                    <td className="px-2 py-1 font-mono">{d.http_status || '-'}</td>
                    <td className="px-2 py-1 text-red-500 max-w-xs truncate" title={d.error}>{d.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDeliveries(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="hook-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this hook?</AlertDialogTitle><AlertDialogDescription>The hook will stop receiving events immediately. Delivery history is preserved.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="hook-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSubPage>
  );
};

export default ManageHooksPage;
